import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DoorFrontIcon from '@mui/icons-material/DoorFront';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import AuthContext from '../../context/authContext';
import useTablePlan from '../../hooks/useTablePlan';

// Digital table plan ("Tischplan"): the branch manager draws their location's
// room once — tables by drag & drop plus walls, doors and free text labels —
// and everyone with the read right sees the same drawing with the current
// occupancy on it. Which tables exist stays on the entertainment
// (maxTableNumber + unusableTables); the plan only records where a number
// stands. Mirrors the leading dashboard's Tischplan view.

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5];
const DEFAULT_CANVAS = { width: 1200, height: 800, gridSize: 20 };

const ELEMENT_DEFAULTS = {
  wall: { width: 240, height: 16, text: '' },
  door: { width: 80, height: 16, text: '' },
  text: { width: 160, height: 32, text: 'Beschriftung' },
};

const ELEMENT_LABELS = { wall: 'Wand', door: 'Tür', text: 'Text' };

const snap = (value, gridSize) =>
  gridSize > 0 ? Math.round(value / gridSize) * gridSize : Math.round(value);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const formatTime = iso => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

const TablePlan = () => {
  const authCtx = useContext(AuthContext);
  const canView = authCtx.checkRight('tablePlan:read') || authCtx.checkRight('tablePlan:read:own');
  const canManage =
    authCtx.checkRight('tablePlan:manage') || authCtx.checkRight('tablePlan:manage:own');

  const {
    getPlannableEntertainments,
    getTablePlan,
    saveTablePlan,
    resetTablePlan,
    getOccupancy,
  } = useTablePlan();

  const [entertainments, setEntertainments] = useState([]);
  const [entertainmentId, setEntertainmentId] = useState('');
  const [plan, setPlan] = useState(null);
  const [occupancy, setOccupancy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('view');
  const [zoomIndex, setZoomIndex] = useState(2);
  const [selection, setSelection] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const surfaceRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  // The API functions are recreated on every render of the hook; keeping them in
  // refs lets the effects depend on the ids alone (same pattern as OrderQrCodes).
  const apiRef = useRef({ getPlannableEntertainments, getTablePlan, getOccupancy });
  useEffect(() => {
    apiRef.current = { getPlannableEntertainments, getTablePlan, getOccupancy };
  }, [getPlannableEntertainments, getTablePlan, getOccupancy]);

  useEffect(() => {
    if (!canView) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await apiRef.current.getPlannableEntertainments();
        if (cancelled) return;
        setEntertainments(list || []);
        if ((list || []).length > 0) setEntertainmentId(list[0]._id);
      } catch {
        // The hook already showed a toast.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  useEffect(() => {
    if (!entertainmentId) return undefined;
    let cancelled = false;
    setLoading(true);
    setSelection(null);
    setIsDirty(false);
    setMode('view');
    (async () => {
      try {
        const data = await apiRef.current.getTablePlan(entertainmentId);
        if (!cancelled) setPlan(data);
      } catch {
        // The hook already showed a toast.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entertainmentId]);

  // Occupancy is only meaningful while looking at the plan, and it keeps up with
  // check-ins on its own so the counter does not have to reload the page.
  useEffect(() => {
    if (!entertainmentId || mode !== 'view') {
      setOccupancy([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiRef.current.getOccupancy(entertainmentId);
        if (!cancelled) setOccupancy(data?.occupancy || []);
      } catch {
        // The hook already showed a toast.
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [entertainmentId, mode]);

  const canvas = plan?.canvas || DEFAULT_CANVAS;
  const tables = useMemo(() => plan?.tables || [], [plan]);
  const elements = useMemo(() => plan?.elements || [], [plan]);
  const zoom = ZOOM_STEPS[zoomIndex];
  const editable = mode === 'edit' && canManage;

  const unusableTables = useMemo(
    () => new Set(plan?.unusableTables || []),
    [plan]
  );

  const occupancyByTable = useMemo(() => {
    const map = new Map();
    if (mode !== 'view') return map;
    (occupancy || []).forEach(entry => {
      const list = map.get(entry.tableNumber);
      if (list) list.push(entry);
      else map.set(entry.tableNumber, [entry]);
    });
    return map;
  }, [mode, occupancy]);

  const unplacedNumbers = useMemo(() => {
    const placed = new Set(tables.map(t => t.tableNumber));
    return (plan?.availableTableNumbers || []).filter(n => !placed.has(n));
  }, [plan, tables]);

  const updatePlan = useCallback(patch => {
    setPlan(previous => (previous ? { ...previous, ...patch } : previous));
    setIsDirty(true);
  }, []);

  const patchTable = useCallback(
    (index, patch) =>
      updatePlan({ tables: tables.map((t, i) => (i === index ? { ...t, ...patch } : t)) }),
    [tables, updatePlan]
  );

  const patchElement = useCallback(
    (index, patch) =>
      updatePlan({ elements: elements.map((e, i) => (i === index ? { ...e, ...patch } : e)) }),
    [elements, updatePlan]
  );

  // A free spot for a newly added item, stepped diagonally so several additions
  // in a row do not stack on the same pixel.
  const nextFreeSpot = count => ({
    x: Math.min(40 + (count % 8) * 100, canvas.width - 120),
    y: Math.min(40 + Math.floor(count / 8) * 100, canvas.height - 120),
  });

  const handleAddTable = tableNumber => {
    const spot = nextFreeSpot(tables.length);
    updatePlan({
      tables: [
        ...tables,
        {
          tableNumber,
          x: spot.x,
          y: spot.y,
          width: 80,
          height: 80,
          rotation: 0,
          shape: 'rect',
          seats: null,
        },
      ],
    });
    setSelection({ type: 'table', index: tables.length });
    setAddMenuAnchor(null);
  };

  const handleAddAllTables = () => {
    if (unplacedNumbers.length === 0) return;
    const added = unplacedNumbers.map((tableNumber, offset) => {
      const spot = nextFreeSpot(tables.length + offset);
      return {
        tableNumber,
        x: spot.x,
        y: spot.y,
        width: 80,
        height: 80,
        rotation: 0,
        shape: 'rect',
        seats: null,
      };
    });
    updatePlan({ tables: [...tables, ...added] });
    setSelection(null);
    setAddMenuAnchor(null);
  };

  const handleAddElement = kind => {
    const spot = nextFreeSpot(elements.length);
    const defaults = ELEMENT_DEFAULTS[kind];
    updatePlan({
      elements: [
        ...elements,
        {
          kind,
          x: spot.x,
          y: spot.y,
          width: defaults.width,
          height: defaults.height,
          rotation: 0,
          text: defaults.text,
          fontSize: 16,
        },
      ],
    });
    setSelection({ type: 'element', index: elements.length });
  };

  const handleDeleteSelected = () => {
    if (!selection) return;
    if (selection.type === 'table') {
      updatePlan({ tables: tables.filter((_, i) => i !== selection.index) });
    } else {
      updatePlan({ elements: elements.filter((_, i) => i !== selection.index) });
    }
    setSelection(null);
  };

  const handleSave = async () => {
    if (!entertainmentId) return;
    setSaving(true);
    try {
      const saved = await saveTablePlan(entertainmentId, { canvas, tables, elements });
      setPlan(saved);
      setIsDirty(false);
      toast.success('Tischplan gespeichert.');
    } catch {
      // The hook already showed a toast.
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!entertainmentId) return;
    setSaving(true);
    try {
      const emptied = await resetTablePlan(entertainmentId);
      setPlan(emptied);
      setSelection(null);
      setIsDirty(false);
      toast.success('Tischplan zurückgesetzt.');
    } catch {
      // The hook already showed a toast.
    } finally {
      setSaving(false);
    }
  };

  const toCanvasPoint = (clientX, clientY) => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
  };

  const handleDragStart = (event, target, item) => {
    if (!editable) return;
    event.stopPropagation();
    setSelection(target);
    const point = toCanvasPoint(event.clientX, event.clientY);
    dragRef.current = { offsetX: point.x - item.x, offsetY: point.y - item.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event, target, item) => {
    if (!dragRef.current || !editable) return;
    const point = toCanvasPoint(event.clientX, event.clientY);
    const next = {
      x: clamp(
        snap(point.x - dragRef.current.offsetX, canvas.gridSize),
        0,
        canvas.width - item.width
      ),
      y: clamp(
        snap(point.y - dragRef.current.offsetY, canvas.gridSize),
        0,
        canvas.height - item.height
      ),
    };
    if (target.type === 'table') patchTable(target.index, next);
    else patchElement(target.index, next);
  };

  const handlePointerRelease = (event, ref) => {
    // eslint-disable-next-line no-param-reassign
    ref.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleResizeStart = (event, target, item) => {
    event.stopPropagation();
    setSelection(target);
    const point = toCanvasPoint(event.clientX, event.clientY);
    resizeRef.current = {
      startWidth: item.width,
      startHeight: item.height,
      startX: point.x,
      startY: point.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleResizeMove = (event, target) => {
    const resize = resizeRef.current;
    if (!resize) return;
    const point = toCanvasPoint(event.clientX, event.clientY);
    const next = {
      width: clamp(snap(resize.startWidth + (point.x - resize.startX), canvas.gridSize), 20, 2000),
      height: clamp(
        snap(resize.startHeight + (point.y - resize.startY), canvas.gridSize),
        12,
        2000
      ),
    };
    if (target.type === 'table') patchTable(target.index, next);
    else patchElement(target.index, next);
  };

  const isSelected = target =>
    selection && selection.type === target.type && selection.index === target.index;

  const selectedTable = selection?.type === 'table' ? tables[selection.index] : null;
  const selectedElement = selection?.type === 'element' ? elements[selection.index] : null;
  const selectedGeometry = selectedTable || selectedElement;
  const selectionKey = selection ? `${selection.type}-${selection.index}` : 'none';

  if (!canView) {
    return <Navigate to='/' replace />;
  }

  const gridBackground =
    editable && canvas.gridSize > 0
      ? {
          backgroundImage:
            'linear-gradient(to right, rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.08) 1px, transparent 1px)',
          backgroundSize: `${canvas.gridSize}px ${canvas.gridSize}px`,
        }
      : {};

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' sx={{ mb: 1, fontWeight: 700 }}>
        Tischplan
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Der Tischplan zeigt den Raum eines Standorts von oben: wo die Tische stehen, ergänzt um
        Wände, Türen und eigene Beschriftungen. In der Ansicht ist zu sehen, welcher Tisch gerade
        belegt ist.
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
        flexWrap='wrap'
        useFlexGap
      >
        <FormControl size='small' sx={{ minWidth: 260 }}>
          <InputLabel id='table-plan-entertainment-label'>Standort</InputLabel>
          <Select
            labelId='table-plan-entertainment-label'
            label='Standort'
            value={entertainmentId}
            onChange={event => setEntertainmentId(event.target.value)}
            disabled={entertainments.length === 0}
          >
            {entertainments.map(item => (
              <MenuItem key={item._id} value={item._id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {canManage && (
          <ToggleButtonGroup
            size='small'
            exclusive
            value={mode}
            onChange={(event, value) => value && setMode(value)}
          >
            <ToggleButton value='view'>Ansicht</ToggleButton>
            <ToggleButton value='edit'>Bearbeiten</ToggleButton>
          </ToggleButtonGroup>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
          <Tooltip title='Verkleinern'>
            <span>
              <IconButton
                size='small'
                disabled={zoomIndex === 0}
                onClick={() => setZoomIndex(i => Math.max(0, i - 1))}
              >
                <ZoomOutIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title='Vergrößern'>
            <span>
              <IconButton
                size='small'
                disabled={zoomIndex === ZOOM_STEPS.length - 1}
                onClick={() => setZoomIndex(i => Math.min(ZOOM_STEPS.length - 1, i + 1))}
              >
                <ZoomInIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>

          {editable && (
            <>
              <Button
                size='small'
                variant='outlined'
                startIcon={<AddIcon />}
                disabled={unplacedNumbers.length === 0}
                onClick={event => setAddMenuAnchor(event.currentTarget)}
              >
                Tisch hinzufügen
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                PaperProps={{ sx: { maxHeight: 320 } }}
              >
                <MenuItem onClick={handleAddAllTables}>
                  Alle {unplacedNumbers.length} Tische platzieren
                </MenuItem>
                <Divider />
                {unplacedNumbers.map(number => (
                  <MenuItem key={number} onClick={() => handleAddTable(number)}>
                    Tisch {number}
                    {unusableTables.has(number) ? ' (defekt)' : ''}
                  </MenuItem>
                ))}
              </Menu>
              <Button
                size='small'
                variant='outlined'
                startIcon={<HorizontalRuleIcon />}
                onClick={() => handleAddElement('wall')}
              >
                Wand
              </Button>
              <Button
                size='small'
                variant='outlined'
                startIcon={<DoorFrontIcon />}
                onClick={() => handleAddElement('door')}
              >
                Tür
              </Button>
              <Button
                size='small'
                variant='outlined'
                startIcon={<TextFieldsIcon />}
                onClick={() => handleAddElement('text')}
              >
                Text
              </Button>
              <Button
                size='small'
                variant='outlined'
                color='warning'
                startIcon={<RestartAltIcon />}
                disabled={saving || (!plan?.exists && !isDirty)}
                onClick={handleReset}
              >
                Zurücksetzen
              </Button>
              <Button
                size='small'
                variant='contained'
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={!isDirty || saving}
                onClick={handleSave}
              >
                Speichern
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {!loading && entertainments.length === 0 && (
        <Alert severity='info'>
          Für deine Standorte sind keine Tische hinterlegt. Die Anzahl der Tische wird in der
          Standortverwaltung gepflegt (Feld „Maximale Tischnummer“); erst danach lässt sich ein
          Tischplan zeichnen.
        </Alert>
      )}

      {plan && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
          {tables.length} von {plan.maxTableNumber} Tischen platziert
          {mode === 'view' && occupancyByTable.size > 0
            ? ` · ${occupancyByTable.size} belegt`
            : ''}
          {isDirty ? ' · nicht gespeichert' : ''}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        plan && (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems='flex-start'>
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                bgcolor: 'action.hover',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: canvas.width * zoom,
                  height: canvas.height * zoom,
                  mx: 'auto',
                }}
              >
                <Box
                  ref={surfaceRef}
                  onPointerDown={() => editable && setSelection(null)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: canvas.width,
                    height: canvas.height,
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    ...gridBackground,
                  }}
                >
                  {elements.map((element, index) => {
                    const target = { type: 'element', index };
                    const selected = isSelected(target);
                    return (
                      <Box
                        key={`element-${index}`}
                        onPointerDown={event => handleDragStart(event, target, element)}
                        onPointerMove={event => handleDragMove(event, target, element)}
                        onPointerUp={event => handlePointerRelease(event, dragRef)}
                        onPointerCancel={event => handlePointerRelease(event, dragRef)}
                        sx={{
                          position: 'absolute',
                          left: element.x,
                          top: element.y,
                          width: element.width,
                          height: element.height,
                          transform: element.rotation ? `rotate(${element.rotation}deg)` : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          userSelect: 'none',
                          touchAction: editable ? 'none' : 'auto',
                          cursor: editable ? 'move' : 'default',
                          outline: selected ? '2px solid' : 'none',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                          ...(element.kind === 'wall' && { bgcolor: 'text.primary' }),
                          ...(element.kind === 'door' && {
                            border: '2px dashed',
                            borderColor: 'warning.main',
                            bgcolor: 'warning.light',
                            opacity: 0.9,
                          }),
                        }}
                      >
                        {element.kind === 'text' ? (
                          <Typography
                            sx={{
                              fontSize: element.fontSize,
                              fontWeight: 500,
                              width: '100%',
                              textAlign: 'center',
                              lineHeight: 1.1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {element.text || 'Text'}
                          </Typography>
                        ) : (
                          element.text && (
                            <Typography
                              variant='caption'
                              sx={{ px: 0.5, color: 'background.paper', fontWeight: 500 }}
                            >
                              {element.text}
                            </Typography>
                          )
                        )}
                        {editable && selected && (
                          <Box
                            onPointerDown={event => handleResizeStart(event, target, element)}
                            onPointerMove={event => handleResizeMove(event, target)}
                            onPointerUp={event => handlePointerRelease(event, resizeRef)}
                            onPointerCancel={event => handlePointerRelease(event, resizeRef)}
                            sx={{
                              position: 'absolute',
                              right: -6,
                              bottom: -6,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              border: '2px solid',
                              borderColor: 'background.paper',
                              cursor: 'se-resize',
                              touchAction: 'none',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}

                  {tables.map((table, index) => {
                    const target = { type: 'table', index };
                    const selected = isSelected(target);
                    const guests = occupancyByTable.get(table.tableNumber) || [];
                    const isUnusable = unusableTables.has(table.tableNumber);
                    const isOccupied = guests.length > 0;
                    const guest = guests[0];
                    return (
                      <Box
                        key={`table-${table.tableNumber}`}
                        title={
                          isOccupied
                            ? guests
                                .map(
                                  entry =>
                                    `${entry.name || 'Buchung'} · ${formatTime(entry.startTime)}–${formatTime(entry.endTime)}${
                                      entry.participantCount
                                        ? ` · ${entry.participantCount} P.`
                                        : ''
                                    }`
                                )
                                .join('\n')
                            : undefined
                        }
                        onPointerDown={event => handleDragStart(event, target, table)}
                        onPointerMove={event => handleDragMove(event, target, table)}
                        onPointerUp={event => handlePointerRelease(event, dragRef)}
                        onPointerCancel={event => handlePointerRelease(event, dragRef)}
                        sx={{
                          position: 'absolute',
                          left: table.x,
                          top: table.y,
                          width: table.width,
                          height: table.height,
                          transform: table.rotation ? `rotate(${table.rotation}deg)` : 'none',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          p: 0.5,
                          textAlign: 'center',
                          userSelect: 'none',
                          touchAction: editable ? 'none' : 'auto',
                          cursor: editable ? 'move' : 'default',
                          borderRadius: table.shape === 'circle' ? '50%' : 1,
                          border: '2px solid',
                          borderColor: isUnusable
                            ? 'error.main'
                            : isOccupied
                              ? 'success.main'
                              : 'divider',
                          bgcolor: isUnusable
                            ? 'error.light'
                            : isOccupied
                              ? 'success.light'
                              : 'background.paper',
                          outline: selected ? '2px solid' : 'none',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        }}
                      >
                        <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
                          {table.tableNumber}
                        </Typography>
                        {isUnusable ? (
                          <Typography sx={{ fontSize: 10, lineHeight: 1.2 }}>defekt</Typography>
                        ) : isOccupied && guest ? (
                          <>
                            <Typography
                              sx={{
                                fontSize: 10,
                                lineHeight: 1.2,
                                width: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {guest.name || 'Buchung'}
                            </Typography>
                            <Typography sx={{ fontSize: 10, lineHeight: 1.2, opacity: 0.8 }}>
                              {formatTime(guest.startTime)}
                              {guest.participantCount ? ` · ${guest.participantCount} P.` : ''}
                            </Typography>
                          </>
                        ) : (
                          table.seats && (
                            <Typography sx={{ fontSize: 10, lineHeight: 1.2, opacity: 0.7 }}>
                              {table.seats} Plätze
                            </Typography>
                          )
                        )}
                        {editable && selected && (
                          <Box
                            onPointerDown={event => handleResizeStart(event, target, table)}
                            onPointerMove={event => handleResizeMove(event, target)}
                            onPointerUp={event => handlePointerRelease(event, resizeRef)}
                            onPointerCancel={event => handlePointerRelease(event, resizeRef)}
                            sx={{
                              position: 'absolute',
                              right: 0,
                              bottom: 0,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              border: '2px solid',
                              borderColor: 'background.paper',
                              cursor: 'se-resize',
                              touchAction: 'none',
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Card sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
              <CardContent>
                <Typography sx={{ fontWeight: 700, mb: 2 }}>
                  {editable ? 'Element' : 'Belegung'}
                </Typography>

                {editable ? (
                  !selectedGeometry ? (
                    <Typography variant='body2' color='text.secondary'>
                      Kein Element ausgewählt. Klicke im Plan auf einen Tisch, eine Wand, eine Tür
                      oder ein Textfeld, um es zu bearbeiten.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      <Typography variant='subtitle2'>
                        {selectedTable
                          ? `Tisch ${selectedTable.tableNumber}`
                          : ELEMENT_LABELS[selectedElement.kind]}
                      </Typography>

                      {selectedElement && (
                        <TextField
                          size='small'
                          label={
                            selectedElement.kind === 'text'
                              ? 'Beschriftung'
                              : 'Beschriftung (optional)'
                          }
                          defaultValue={selectedElement.text}
                          inputProps={{ maxLength: 120 }}
                          key={`text-${selectionKey}`}
                          onBlur={event =>
                            patchElement(selection.index, { text: event.target.value })
                          }
                        />
                      )}

                      {selectedElement?.kind === 'text' && (
                        <TextField
                          size='small'
                          type='number'
                          label='Schriftgröße'
                          defaultValue={selectedElement.fontSize}
                          key={`fs-${selectionKey}`}
                          onBlur={event =>
                            patchElement(selection.index, {
                              fontSize: Number(event.target.value) || selectedElement.fontSize,
                            })
                          }
                        />
                      )}

                      {selectedTable && (
                        <>
                          <FormControl size='small'>
                            <InputLabel id='table-plan-shape-label'>Form</InputLabel>
                            <Select
                              labelId='table-plan-shape-label'
                              label='Form'
                              value={selectedTable.shape}
                              onChange={event =>
                                patchTable(selection.index, { shape: event.target.value })
                              }
                            >
                              <MenuItem value='rect'>Eckig</MenuItem>
                              <MenuItem value='circle'>Rund</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size='small'
                            type='number'
                            label='Plätze (optional)'
                            defaultValue={selectedTable.seats ?? ''}
                            key={`seats-${selectionKey}`}
                            onBlur={event =>
                              patchTable(selection.index, {
                                seats:
                                  event.target.value === '' ? null : Number(event.target.value),
                              })
                            }
                          />
                        </>
                      )}

                      <Stack direction='row' spacing={1}>
                        <TextField
                          size='small'
                          type='number'
                          label='Breite'
                          key={`w-${selectionKey}-${selectedGeometry.width}`}
                          defaultValue={selectedGeometry.width}
                          onBlur={event => {
                            const width = Number(event.target.value) || selectedGeometry.width;
                            if (selectedTable) patchTable(selection.index, { width });
                            else patchElement(selection.index, { width });
                          }}
                        />
                        <TextField
                          size='small'
                          type='number'
                          label='Höhe'
                          key={`h-${selectionKey}-${selectedGeometry.height}`}
                          defaultValue={selectedGeometry.height}
                          onBlur={event => {
                            const height = Number(event.target.value) || selectedGeometry.height;
                            if (selectedTable) patchTable(selection.index, { height });
                            else patchElement(selection.index, { height });
                          }}
                        />
                      </Stack>

                      <TextField
                        size='small'
                        type='number'
                        label='Drehung (Grad)'
                        inputProps={{ step: 15 }}
                        key={`r-${selectionKey}-${selectedGeometry.rotation}`}
                        defaultValue={selectedGeometry.rotation}
                        onBlur={event => {
                          const rotation = Number(event.target.value) || 0;
                          if (selectedTable) patchTable(selection.index, { rotation });
                          else patchElement(selection.index, { rotation });
                        }}
                      />

                      <Button
                        variant='outlined'
                        color='error'
                        size='small'
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteSelected}
                      >
                        {selectedTable ? 'Tisch aus dem Plan nehmen' : 'Element löschen'}
                      </Button>
                    </Stack>
                  )
                ) : occupancyByTable.size === 0 ? (
                  <Typography variant='body2' color='text.secondary'>
                    Gerade ist kein Tisch belegt. Sobald eine Buchung mit Tischzuordnung läuft,
                    erscheint sie hier und im Plan.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {[...occupancyByTable.entries()]
                      .sort((a, b) => a[0] - b[0])
                      .map(([tableNumber, entries]) => (
                        <Box key={tableNumber}>
                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                            Tisch {tableNumber}
                          </Typography>
                          {entries.map(entry => (
                            <Typography
                              key={entry.bookingId}
                              variant='body2'
                              color='text.secondary'
                            >
                              {entry.name || 'Buchung'}
                              {entry.participantCount ? ` · ${entry.participantCount} P.` : ''}
                              {entry.isPresent ? ' · anwesend' : ''}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        )
      )}
    </Box>
  );
};

export default TablePlan;
